import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { FiRefreshCw, FiX } from 'react-icons/fi';

import PendingProductsTable from '../../components/AdminProductInspection/PendingProductsTable';
import Button from '../../components/Button';
import Pagination from '../../components/Pagination';
import { formatDate } from '../../utils/jalaliDate';
import { productReviewAdminApi } from '../../services/productReviewAdminApi';

const ContentInspectionManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form states (تغییرات کاربر قبل از سابمیت)
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Applied filter states (فیلترهای اعمال شده روی جدول و API)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Ordering state - پیش‌فرض قدیمی‌ترین طبق درخواست (created_at)
  const [ordering, setOrdering] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('ordering') || 'created_at';
  });

  const SORT_OPTIONS = [
    { label: 'قدیمی‌ترین', value: 'created_at' },
    { label: 'جدیدترین', value: '-created_at' },
  ];

  const toGregorianISO = (date) => {
    if (!date) return '';
    try {
      return date.toDate().toISOString();
    } catch {
      return '';
    }
  };

  const buildQueryParams = useCallback((pageValue = page) => {
    const params = new URLSearchParams();

    params.append('page', pageValue);
    params.append('page_size', pageSize);

    if (appliedSearchTerm) params.append('search', appliedSearchTerm);
    if (appliedStartDate) params.append('start_date', toGregorianISO(appliedStartDate));
    if (appliedEndDate) params.append('end_date', toGregorianISO(appliedEndDate));
    if (ordering) params.append('ordering', ordering);

    return params.toString();
  }, [page, pageSize, appliedSearchTerm, appliedStartDate, appliedEndDate, ordering]);

  const fetchPendingProducts = useCallback(async (pageValue = page) => {
    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryParams(pageValue);
      // تبدیل کوئری استرینگ به آبجکت ساده برای پاس دادن به متد API
      const paramsObj = Object.fromEntries(new URLSearchParams(queryString));

      const response = await productReviewAdminApi.getPendingProducts(paramsObj);

      // مطابقت با ساختار پاسخ پیچیده یا خام DRF
      const responseData = response.data || response;

      if (Array.isArray(responseData)) {
        setProducts(responseData);
        setTotalCount(responseData.length);
      } else {
        setProducts(responseData.results || []);
        setTotalCount(responseData.count || 0);
      }
    } catch (error) {
      if (error.code === 'NO_INTERNET') {
        setError(error.message);
      } else {
        setError('قطع اینترنت یا خطای شبکه');
      }
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, page]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderingValue = params.get('ordering') || 'created_at';
    setOrdering(orderingValue);
  }, [location.search]);

  useEffect(() => {
    fetchPendingProducts(page);
  }, [fetchPendingProducts, page]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleOrderingChange = useCallback(
    (value) => {
      setOrdering(value);
      setPage(1);

      const params = new URLSearchParams(location.search);
      params.set('ordering', value);

      navigate({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [location.search, location.pathname, navigate]
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearchTerm(searchTerm);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);

    setAppliedSearchTerm('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setPage(1);
  };

  const handleViewDetails = (productId) => {
    navigate(`/admin-panel/product-incpection/${productId}`);
  };

  return (
    <div className="p-6">
      {/* هدر صفحه */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">کالاهای در انتظار بازرسی</h2>
          <p className="text-gray-600 text-sm mt-1">
            تعداد: <span className="font-bold">{totalCount}</span> کالا
          </p>
        </div>
        <button
          onClick={() => fetchPendingProducts(page)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
          title="بارگذاری مجدد"
        >
          <FiRefreshCw className="h-5 w-5 ml-1" />
          <span className="text-sm">بارگذاری مجدد</span>
        </button>
      </div>

      {/* فرم فیلتر و جستجو */}
      <form onSubmit={handleSearchSubmit} className="w-full my-6 mr-[8px]">
        <div
          className="
            grid grid-cols-1 gap-3
            sm:grid-cols-2
            lg:grid-cols-[2fr_1fr_1fr_auto_auto]
            items-center
          "
        >
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="جستجو بر اساس نام، کد، برند، تأمین‌کننده..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <DatePicker
            value={startDate}
            onChange={setStartDate}
            calendar={persian}
            locale={persian_fa}
            placeholder="از تاریخ"
            inputClass="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />

          <DatePicker
            value={endDate}
            onChange={setEndDate}
            calendar={persian}
            locale={persian_fa}
            placeholder="تا تاریخ"
            inputClass="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />

          <div className="flex items-center gap-2 w-full lg:w-auto">
            <Button type="submit" className="w-full lg:w-auto">
              جستجو
            </Button>

            {(searchTerm || startDate || endDate) && (
              <button
                type="button"
                onClick={handleClearFilters}
                title="پاک کردن فیلترها"
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-all"
              >
                <FiX size={18} />
              </button>
            )}
          </div>
        </div>
      </form>

      {/* دکمه‌های مرتب‌سازی */}
      <div className="mb-[16px] flex items-center gap-2 border-b pb-4">
        <span className="text-sm text-gray-500 ml-2">مرتب‌سازی:</span>
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleOrderingChange(option.value)}
            className={`px-3 py-1 text-sm rounded-md border transition ${
              ordering === option.value
                ? 'bg-[#F5F5F5] text-[#1E3A5F] font-semibold border-[#1E3A5F]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* نمایش وضعیت لودینگ / خطا / جدول */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-600">در حال دریافت محصولات...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <p className="font-bold mb-2">خطا در دریافت اطلاعات</p>
            <p className="text-sm mb-4">{error}</p>
            <Button variant="secondary" onClick={() => fetchPendingProducts(page)}>
              تلاش مجدد
            </Button>
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">محصولی یافت نشد</h3>
            <p className="mt-1 text-gray-500">با تغییر فیلترها دوباره امتحان کنید.</p>
          </div>
        ) : (
          <PendingProductsTable
            products={products}
            formatDate={formatDate}
            onViewDetails={handleViewDetails}
          />
        )}
      </div>

      {/* بخش صفحه‌بندی */}
      {!loading && !error && products.length > 0 && (
        <div className="mt-6">
          <div className="mb-4 text-center text-sm text-gray-700">
            نمایش <span className="font-medium">{products.length}</span> محصول از{' '}
            <span className="font-medium">
              {new Intl.NumberFormat('fa-IR').format(totalCount)}
            </span>
          </div>

          <div className="flex justify-center">
            <Pagination
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentInspectionManagement;
